// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { bootstrapActionTypes, initActionTypes } from '@makemydeal/dr-shared-store';
import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';

// utils
import type { StateTree } from '@makemydeal/dr-dash-types';
import { applyDealerOverrides } from '../actions/offerRedux';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { isPaymentMiddlewareReady } from './paymentMiddleware';

/**
 * US2014779: Quote Mode (QQ) bootstraps an empty, dealer-default deal that has never been priced
 * by the real payment engine. Without a persisted DXG record, no field has been touched yet, so
 * inline validation (isPaymentError / lastFailureAction, see useValidationGuard) stays in its
 * default "no error" state until the shopper edits a field and a real payment call round-trips.
 *
 * This middleware forces one "priming" payment recalculation right after init so Save/Save as
 * Draft can surface payment validation errors right after first load, not only after a subsequent
 * field change.
 *
 * Single responsibility: this file owns ONLY the Quote Mode initial-priming concern. It depends on
 * paymentMiddleware's public `isPaymentMiddlewareReady()` contract rather than reaching into its
 * internal cache, so the two middleware stay decoupled and independently testable.
 *
 * Ordering note: INIT_SUCCESS (deal/offer hydrated) and BOOTSTRAP_SUCCESS (payment engine wired up)
 * are two independent dispatches whose relative order is not guaranteed - INIT_SUCCESS has been
 * observed to arrive first locally. This middleware runs the same idempotent `primeIfNeeded` check
 * on both actions and fires as soon as both conditions are met, regardless of which one happens
 * first. Because both entry points re-evaluate the live store (current session id + payment
 * readiness) rather than any remembered "pending" state, there is nothing to leave stale and no
 * risk of priming a session that is no longer current: whichever dispatch finally satisfies both
 * conditions primes the session that is current at that moment.
 */
let primedSessionId: string | undefined;

export const quoteModeInitialPaymentMiddlewareClearCache = (): void => {
    primedSessionId = undefined;
};

const primeIfNeeded = (store: ReduxStore<StateTree>): void => {
    const state = store.getState();

    if (!offerReduxSelectors.getIsDealXgRecordMissed(state)) {
        return;
    }

    const sessionId = offerReduxSelectors.getSessionId(state);
    if (!sessionId || sessionId === primedSessionId) {
        return;
    }

    // Payment engine not wired up yet (INIT_SUCCESS beat BOOTSTRAP_SUCCESS, or the session id
    // isn't in state yet). Record nothing: BOOTSTRAP_SUCCESS re-runs this check unconditionally
    // and will prime once both the session id and readiness are satisfied.
    if (!isPaymentMiddlewareReady()) {
        return;
    }

    primedSessionId = sessionId;
    store.dispatch(applyDealerOverrides());
};

export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    // Let reducers process the action first so state.offer (dealXgId, sessionId, etc.) and any
    // payment-middleware bootstrap side effects are already reflected in store.getState().
    next(action);

    switch (action.type) {
        // Either dispatch can be the one that finally satisfies both conditions (deal hydrated with
        // a session id + payment engine ready). primeIfNeeded is idempotent - guarded by
        // primedSessionId - so running it on both is safe, and it always reads the current session,
        // so a session that only appears (or changes) by BOOTSTRAP_SUCCESS is still handled.
        case initActionTypes.INIT_SUCCESS:
        case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
            primeIfNeeded(store);
            break;
        }
        default:
            break;
    }
};
